// §4.14 THE DEAL, /sales/opportunities/$opportunityId (Module 09, P3-E2 PR
// E2-1; D-312).
//
// ONE READ, `GET /v1/opportunities/{id}`, paints the header, the Details card
// and the Quotes card. A deal outside the reader's narrowing answers the same
// 404 as one that does not exist, and both read "This deal is not available to
// you" (a stable fact, so no Retry).
//
// THE VERBS FOLLOW THE DEAL'S OWN `mutable` (§7 narrowing 16): Move, Mark won
// and Mark lost on an open deal, Reopen on a closed one where
// `reopenOffered` allows, all through `DealActions`, which the deal peek
// renders too. The Details card edits the title, the value (a value reader),
// the close month and, for a literal admin, the owner; a closed deal keeps
// its value and month and says so (the server's 409 `OPPORTUNITY_NOT_OPEN`
// names the same rule). A field the server refuses 400 is named against its
// own field.
//
// THE QUOTES CARD lists the linked quotes this reader's quote door answers
// (the server filters, D-312 ruling 21), each with Unlink while the quote is
// not terminal, because a finished quote's link is part of the deal's history
// (§5's link row). Create quote opens the composer on the deal's customer
// with the deal named (`?customer_id=…&opportunity_id=…`), and Link a quote
// offers the customer's open quotes that belong to no deal.
//
// THE ACCEPTED-QUOTE NOTICE (D-312 ruling 8). A linked quote that is
// converted, whose order stands, on a deal still open, means the customer
// accepted while the deal did not win (a quote accepted through a task of the
// previous release during a deploy). The page says so to every reader, because
// it is a fact, and offers Mark won with that quote to a writer only. The
// sentence has two forms for the same reason: a writer reads that the deal can
// be marked won with the quote, beside the button that does it, and a reader
// who cannot write reads only that the deal is still open, because an
// instruction with no button is one the reader cannot follow. "Its order
// stands" is read from the quote's own detail and then the order's, so the
// notice never offers a win the server refuses with `ORDER_TERMINAL`.
//
// MARK WON NAMES WHAT WON (D-312 ruling 9). Outside the notice, Mark won opens
// a picker of the customer's converted quotes and confirmed orders that the
// server's win door accepts, with "no document" first (`WinDealModal`,
// components/pipeline/deal-dialogs.tsx), so a direct order can back a win and
// the forecast counts its total.
//
// ONE REVEAL. The header names the stage in the reader's language, so the page
// waits for the stages read as well as the deal (design-system §5): painted
// first it printed the pack's code and then the name. A failed stages read
// still opens the page, with the code as the last resort.
//
// UNLINK FOLLOWS THE SERVER'S FINISHED STATUSES (`quoteDealChangeable`,
// lib/pipeline.ts), the set the composer reads too: an accepted quote offers
// none and an expired one, which a revision re-opens, does.
//
// A WON DEAL WHOSE ORDER WAS CANCELLED reads so and offers Reopen
// (`won_order_cancelled`). A reader the order door refuses gets null there,
// and the page offers Reopen and lets the server decide.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { AuditTrailLink } from "../../audit/components/audit-trail-link";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { columnPresent, formatMinor, minorToDecimal, minorUnitDigits } from "../lib/money";
import {
  formatMonth,
  isLostReasonCode,
  lostReasonLabel,
  parseValueInput,
  quoteDealChangeable,
  stageName,
} from "../lib/pipeline";
import { quoteStatusVariant } from "../lib/quotes";
import { valueSortVerdict } from "../lib/value-order";
import {
  canReassignOpportunityOwner,
  canWriteOpportunities,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useOrder, useQuote } from "../hooks/quote-queries";
import {
  useLinkQuoteOpportunity,
  useOpportunity,
  usePipelineStages,
  useUpdateOpportunity,
  type OpportunityDetail,
  type OpportunityLinkedQuote,
  type PipelineStage,
} from "../hooks/opportunity-queries";
import { DealActions, type WinWithQuote } from "../components/pipeline/deal-actions";
import {
  DEAL_TITLE_MAX,
  LinkQuoteModal,
  RefusalLine,
  dealFieldError,
} from "../components/pipeline/deal-dialogs";
import {
  DealStatusChip,
  IdleChip,
  NextStepCell,
  StageText,
} from "../components/pipeline/pipeline-parts";
import { holderPrefill, SubjectTasksCard } from "../components/peek/subject-tasks";
import { NewTaskModal } from "../components/sales-tasks";
import { bookClientPager } from "../hooks/book-pages";

export function SalesOpportunityDetailScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const params: Record<string, string | undefined> = useParams({ strict: false });
  const id = params["opportunityId"] ?? "";
  const refused = isSalesReadRefused(perms, perms.canOpenPipeline);
  const deal = useOpportunity(id, !refused);
  const stages = usePipelineStages(!refused);

  useEffect(() => {
    if (deal.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [deal.error, navigate]);

  if (refused) {
    return (
      <section className="sales-deal-screen" data-testid="sales-deal-screen">
        <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pipeline")} />
      </section>
    );
  }
  const stagesSettled =
    stages.data !== undefined || stages.error !== null || stages.fetchStatus === "paused";
  if (deal.data === undefined || (deal.error === null && !stagesSettled)) {
    const gone =
      deal.error instanceof RequestError &&
      (deal.error.httpStatus === 404 || deal.error.httpStatus === 403);
    return (
      <section className="sales-deal-screen" data-testid="sales-deal-screen">
        {gone ? (
          <div data-testid="sales-deal-not-available">
            <EmptyState
              title={t("sales.pipeline.deal.notAvailableTitle")}
              body={t("sales.pipeline.deal.notAvailableBody")}
            />
          </div>
        ) : deal.fetchStatus === "paused" ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : deal.error !== null && !(deal.error instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("sales.pipeline.deal.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(deal.error)}
            onRetry={() => {
              void deal.refetch();
            }}
          />
        ) : (
          <div data-testid="sales-deal-loading">
            <CardSkeleton lines={3} label={t("sales.pipeline.deal.loading")} />
            <CardSkeleton lines={4} label={t("sales.pipeline.deal.loading")} />
          </div>
        )}
      </section>
    );
  }
  return <DealPage deal={deal.data} stages={stages.data?.items ?? []} />;
}

function DealPage({
  deal,
  stages,
}: {
  readonly deal: OpportunityDetail;
  readonly stages: readonly PipelineStage[];
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const stage = stages.find((s) => s.id === deal.stage_id);
  const name = stage === undefined ? deal.stage_code : stageName(stage, i18n.language);
  const canWrite = canWriteOpportunities(perms) && deal.mutable;
  const [winWith, setWinWith] = useState<WinWithQuote | undefined>(undefined);
  const [scheduling, setScheduling] = useState(false);
  const holder = {
    kind: "opportunity" as const,
    id: deal.id,
    customerId: deal.customer_id,
    label: deal.opportunity_no,
  };

  return (
    <section className="sales-deal-screen" data-testid="sales-deal-screen" data-deal-id={deal.id}>
      <PageHeader
        title={deal.title}
        crumbs={[
          { label: t("sales.pipeline.title"), to: "/sales/pipeline" },
          { label: t("sales.pipeline.list.title"), to: "/sales/opportunities" },
        ]}
        data-testid="sales-deal-pagehead"
      />

      <div className="sales-deal-header" data-testid="sales-deal-header">
        <div className="sales-360-idline">
          <span className="sales-360-no num" data-testid="sales-deal-no">
            {t("sales.pipeline.deal.number", { no: deal.opportunity_no })}
          </span>
          <DealStatusChip status={deal.status} />
          <StageText name={name} pct={deal.probability_pct} />
          <span className="sales-360-fact" data-testid="sales-deal-in-stage">
            {deal.rotting ? (
              <IdleChip days={deal.idle_days} rotting />
            ) : (
              t("sales.pipeline.deal.inStage", { count: deal.idle_days })
            )}
          </span>
        </div>
        <div className="sales-360-idline">
          <span className="sales-360-fact">
            {t("sales.pipeline.deal.customer")}:{" "}
            {deal.customer_name !== null && perms.canOpenCustomers ? (
              <Link
                to="/sales/customers/$customerId"
                params={{ customerId: deal.customer_id }}
                className="ds-lnk"
                data-testid="sales-deal-customer"
              >
                {deal.customer_name}
              </Link>
            ) : (
              <span data-testid="sales-deal-customer">
                <RefLabel value={deal.customer_name ?? undefined} id={deal.customer_id} />
              </span>
            )}
          </span>
          <span className="sales-360-fact" data-testid="sales-deal-owner">
            {t("sales.pipeline.owner")}: {deal.owner_name ?? t("sales.pipeline.ownerNone")}
          </span>
          <span className="sales-360-fact" data-testid="sales-deal-next">
            {t("sales.pipeline.deal.nextStep")}:{" "}
            <NextStepCell
              next={deal.next_activity}
              needsNextStep={deal.needs_next_step}
              onSchedule={
                perms.has("sales.activity.write") && deal.mutable
                  ? () => {
                      setScheduling(true);
                    }
                  : undefined
              }
              scheduleName={deal.title}
            />
          </span>
        </div>
        <DealActions
          deal={deal}
          stages={stages}
          testPrefix="sales-deal"
          winWith={winWith}
          onWinWithHandled={() => {
            setWinWith(undefined);
          }}
        />
      </div>

      <ClosedFacts deal={deal} />
      {deal.status === "open" ? (
        <AcceptedNotices
          quotes={deal.linked_quotes}
          onWinWith={
            canWrite
              ? (quote) => {
                  setWinWith({ quoteId: quote.id, quoteNo: quote.quote_no });
                }
              : undefined
          }
        />
      ) : null}

      <div className="sales-deal-cards">
        <DetailsCard deal={deal} />
        <QuotesCard deal={deal} canWrite={canWrite} />
        <SubjectTasksCard
          holder={holder}
          title={t("sales.pipeline.deal.tasksTitle")}
          testId="sales-deal-tasks"
          mutable={deal.mutable}
        />
        {deal.source_conversation_id !== null ? (
          <Card className="sales-360-card" title={t("sales.pipeline.deal.sourceTitle")}>
            <p data-testid="sales-deal-source" data-conversation={deal.source_conversation_id}>
              {t("sales.pipeline.deal.sourceConversation")}{" "}
              <Link
                to="/messaging/inbox"
                search={{ c: deal.source_conversation_id }}
                className="ds-lnk"
                data-testid="sales-deal-source-link"
              >
                {t("sales.pipeline.deal.openConversation")}
              </Link>
            </p>
          </Card>
        ) : null}
      </div>

      <AuditTrailLink entityType="sales_opportunity" entityId={deal.id} />

      {scheduling ? (
        <NewTaskModal
          prefill={holderPrefill(holder)}
          onClose={() => {
            setScheduling(false);
          }}
        />
      ) : null}
    </section>
  );
}

/** A closed deal's facts: how it was won, or why it was lost, and when. */
function ClosedFacts({ deal }: { readonly deal: OpportunityDetail }) {
  const { t, i18n } = useTranslation();
  if (deal.status === "open") return null;
  const closedAt =
    deal.closed_at === null ? null : (
      <span className="sales-hint" data-testid="sales-deal-closed-at">
        {t("sales.pipeline.deal.closedAt", {
          when: formatTimestamp(deal.closed_at, i18n.language),
        })}
      </span>
    );
  if (deal.status === "lost") {
    return (
      <Card
        className="sales-360-card"
        data-testid="sales-deal-lost"
        title={t("sales.pipeline.deal.lostReason")}
      >
        <p
          data-testid="sales-deal-lost-reason"
          data-reason={isLostReasonCode(deal.lost_reason_code) ? deal.lost_reason_code : ""}
        >
          {isLostReasonCode(deal.lost_reason_code) ? lostReasonLabel(t, deal.lost_reason_code) : ""}
        </p>
        {deal.lost_note !== null ? (
          <p data-testid="sales-deal-lost-note">
            {t("sales.pipeline.deal.lostNote")}: {deal.lost_note}
          </p>
        ) : null}
        {closedAt}
      </Card>
    );
  }
  const wonLine =
    deal.won_quote_no !== null && deal.won_order_no !== null
      ? t("sales.pipeline.deal.wonWithQuoteAndOrder", {
          quote: deal.won_quote_no,
          order: deal.won_order_no,
        })
      : deal.won_quote_no !== null
        ? t("sales.pipeline.deal.wonWithQuote", { quote: deal.won_quote_no })
        : deal.won_order_no !== null
          ? t("sales.pipeline.deal.wonWithOrder", { order: deal.won_order_no })
          : deal.won_via === "quote_accepted"
            ? t("sales.pipeline.deal.wonByAcceptance")
            : t("sales.pipeline.deal.wonByHand");
  return (
    <div className="sales-deal-won" data-testid="sales-deal-won" data-won-via={deal.won_via ?? ""}>
      <p>{wonLine}</p>
      {closedAt}
      {deal.won_order_cancelled === true ? (
        <p className="sales-banner" role="status" data-testid="sales-deal-won-cancelled">
          {deal.won_order_no !== null
            ? t("sales.pipeline.deal.wonCancelled", { order: deal.won_order_no })
            : t("sales.pipeline.deal.wonCancelledNoNumber")}
        </p>
      ) : null}
    </div>
  );
}

/** One converted linked quote: its order is read, and the notice shows only
 * while that order stands. */
function AcceptedNotice({
  quote,
  onWinWith,
}: {
  readonly quote: OpportunityLinkedQuote;
  /** Absent for a reader who cannot win the deal: the notice stays a fact. */
  readonly onWinWith: ((quote: OpportunityLinkedQuote) => void) | undefined;
}) {
  const { t } = useTranslation();
  const detail = useQuote(quote.id);
  const orderId = detail.data?.converted_order_id ?? "";
  const order = useOrder(orderId);
  if (order.data === undefined || order.data.status === "cancelled") return null;
  return (
    <div
      className="sales-banner"
      role="status"
      data-testid="sales-deal-accepted"
      data-quote-id={quote.id}
    >
      {onWinWith !== undefined
        ? t("sales.pipeline.deal.acceptedNotice", { quote: quote.quote_no })
        : t("sales.pipeline.deal.acceptedNoticeReader", { quote: quote.quote_no })}{" "}
      {onWinWith !== undefined ? (
        <Button
          size="sm"
          data-testid="sales-deal-accepted-win"
          onClick={() => {
            onWinWith(quote);
          }}
        >
          {t("sales.pipeline.deal.markWonWith", { quote: quote.quote_no })}
        </Button>
      ) : null}
    </div>
  );
}

function AcceptedNotices({
  quotes,
  onWinWith,
}: {
  readonly quotes: readonly OpportunityLinkedQuote[];
  readonly onWinWith: ((quote: OpportunityLinkedQuote) => void) | undefined;
}) {
  const converted = quotes.filter((quote) => quote.status === "converted");
  return (
    <>
      {converted.map((quote) => (
        <AcceptedNotice key={quote.id} quote={quote} onWinWith={onWinWith} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------

function DetailsCard({ deal }: { readonly deal: OpportunityDetail }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const update = useUpdateOpportunity(deal.id);
  const canWrite = canWriteOpportunities(perms) && deal.mutable;
  const canNameOwner = canReassignOpportunityOwner(perms);
  const owners = useOwnerIndex(canNameOwner);
  // The value box shows for a reader the payload proves is sent values, and
  // is offered for editing to a reader the mirror does not know is refused.
  const valueSent = deal.expected_value_minor !== undefined;
  const valueEditable = valueSent && valueSortVerdict(perms) !== "refused";
  const open = deal.status === "open";

  const digits = minorUnitDigits(deal.currency, i18n.language);
  const initialValue =
    deal.expected_value_minor === undefined || deal.expected_value_minor === null
      ? ""
      : minorToDecimal(deal.expected_value_minor, digits);
  const [title, setTitle] = useState(deal.title);
  const [valueText, setValueText] = useState(initialValue);
  const [closeMonth, setCloseMonth] = useState(deal.expected_close_month);
  const [owner, setOwner] = useState(deal.owner_user_id);
  const [saved, setSaved] = useState(false);
  // The deal read again (a verb elsewhere on the page): the form follows it.
  useEffect(() => {
    setTitle(deal.title);
    setValueText(initialValue);
    setCloseMonth(deal.expected_close_month);
    setOwner(deal.owner_user_id);
  }, [deal.updated_at, deal.title, initialValue, deal.expected_close_month, deal.owner_user_id]);

  const value = parseValueInput(valueText, deal.currency, i18n.language);
  const trimmed = title.trim();
  const titleError =
    trimmed.length === 0
      ? t("sales.pipeline.create.titleRequired")
      : trimmed.length > DEAL_TITLE_MAX
        ? t("sales.pipeline.create.titleTooLong")
        : undefined;
  const valueError =
    valueEditable && value === undefined ? t("sales.pipeline.create.valueInvalid") : undefined;

  const body: Record<string, unknown> = {};
  if (trimmed !== deal.title) body["title"] = trimmed;
  if (
    valueEditable &&
    open &&
    value !== undefined &&
    value !== (deal.expected_value_minor ?? null)
  ) {
    body["expected_value_minor"] = value;
  }
  if (open && closeMonth !== deal.expected_close_month && closeMonth.length > 0) {
    body["expected_close_month"] = closeMonth;
  }
  if (canNameOwner && owner !== deal.owner_user_id) body["owner_user_id"] = owner;
  const dirty = Object.keys(body).length > 0;

  const fieldLine = (field: string) =>
    dealFieldError(update.error, field) ? (
      <span className="field-error" role="alert" data-testid={`sales-deal-error-${field}`}>
        {t("sales.pipeline.deal.saveFailed")}
      </span>
    ) : null;

  return (
    <Card
      className="sales-360-card"
      data-testid="sales-deal-details"
      title={t("sales.pipeline.deal.detailsTitle")}
    >
      <div className="sales-form">
        {/* The whole row of the form (`.sales-deal-title-field`, sales.css):
            the title is the longest value here and must fit its own box. */}
        <Field label={t("sales.pipeline.deal.titleLabel")} wide className="sales-deal-title-field">
          <input
            value={title}
            readOnly={!canWrite}
            data-testid="sales-deal-title-input"
            onChange={(event) => {
              setSaved(false);
              setTitle(event.target.value);
            }}
          />
          {canWrite && titleError !== undefined ? (
            <span className="field-error" role="alert" data-testid="sales-deal-error-title-client">
              {titleError}
            </span>
          ) : null}
          {fieldLine("title")}
        </Field>
        {valueSent ? (
          <>
            <Field label={t("sales.pipeline.deal.valueLabel")}>
              <input
                value={valueText}
                inputMode="decimal"
                readOnly={!canWrite || !open || !valueEditable}
                data-testid="sales-deal-value-input"
                aria-describedby="sales-deal-value-hint"
                onChange={(event) => {
                  setSaved(false);
                  setValueText(event.target.value);
                }}
              />
              {canWrite && valueError !== undefined ? (
                <span
                  className="field-error"
                  role="alert"
                  data-testid="sales-deal-error-value-client"
                >
                  {valueError}
                </span>
              ) : null}
              {fieldLine("expected_value_minor")}
            </Field>
            <Field label={t("sales.pipeline.deal.currencyLabel")}>
              <input
                value={deal.currency}
                readOnly
                aria-readonly="true"
                data-testid="sales-deal-currency"
              />
            </Field>
            <p className="sales-hint" id="sales-deal-value-hint">
              {t("sales.pipeline.deal.valueHint")}
            </p>
            {deal.weighted_value_minor !== undefined && deal.weighted_value_minor !== null ? (
              <p className="sales-hint num" data-testid="sales-deal-weighted">
                {t("sales.pipeline.weightedValue", {
                  amount: formatMinor(deal.weighted_value_minor, deal.currency, i18n.language),
                })}
              </p>
            ) : null}
          </>
        ) : null}
        <Field label={t("sales.pipeline.deal.closeMonthLabel")}>
          {canWrite && open ? (
            <input
              type="month"
              value={closeMonth}
              data-testid="sales-deal-close-input"
              onChange={(event) => {
                setSaved(false);
                setCloseMonth(event.target.value);
              }}
            />
          ) : (
            <span
              className={deal.close_month_passed ? "sales-valid-urgent" : undefined}
              data-testid="sales-deal-close"
            >
              {formatMonth(deal.expected_close_month, i18n.language)}
            </span>
          )}
          {fieldLine("expected_close_month")}
        </Field>
        {canWrite && canNameOwner ? (
          <Field label={t("sales.pipeline.deal.ownerLabel")}>
            <Select
              value={owner}
              data-testid="sales-deal-owner-select"
              onChange={(event) => {
                setSaved(false);
                setOwner(event.target.value);
              }}
            >
              {!owners.byId.has(deal.owner_user_id) ? (
                <option value={deal.owner_user_id}>
                  {deal.owner_name ?? t("sales.pipeline.ownerNone")}
                </option>
              ) : null}
              {[...owners.byId.entries()].map(([memberId, memberName]) => (
                <option key={memberId} value={memberId}>
                  {memberName}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>
      {canWrite && !open ? (
        <p className="sales-hint" data-testid="sales-deal-closed-fields">
          {t("sales.pipeline.deal.closedFields")}
        </p>
      ) : null}
      {update.isError && !Object.keys(body).some((field) => dealFieldError(update.error, field)) ? (
        <RefusalLine
          error={update.error}
          fallback={t("sales.pipeline.deal.saveFailed")}
          testId="sales-deal-save-failed"
        />
      ) : null}
      {saved ? (
        <p className="sales-hint" role="status" data-testid="sales-deal-saved">
          {t("sales.pipeline.deal.saved")}
        </p>
      ) : null}
      {canWrite ? (
        <div className="sales-modal-actions">
          <Button
            variant="primary"
            data-testid="sales-deal-save"
            disabled={!dirty || titleError !== undefined || valueError !== undefined}
            busy={update.isPending}
            busyLabel={t("sales.pipeline.deal.saving")}
            onClick={() => {
              update.mutate(body, {
                onSuccess: () => {
                  setSaved(true);
                },
              });
            }}
          >
            {t("sales.pipeline.deal.save")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

function QuotesCard({
  deal,
  canWrite,
}: {
  readonly deal: OpportunityDetail;
  readonly canWrite: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const unlink = useLinkQuoteOpportunity();
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const canCreateQuote =
    canWrite && deal.status === "open" && perms.canOpenQuotes && perms.has("sales.quote.write");
  const rows = deal.linked_quotes;

  const columns: DataTableColumn<OpportunityLinkedQuote>[] = [
    {
      key: "quote_no",
      header: t("sales.quotes.colNo"),
      id: true,
      render: (row) =>
        perms.canOpenQuotes ? (
          <Link
            to="/sales/quotes/$quoteId"
            params={{ quoteId: row.id }}
            className="ds-lnk"
            data-testid="sales-deal-quote-link"
          >
            {row.quote_no}
          </Link>
        ) : (
          <span className="num">{row.quote_no}</span>
        ),
    },
    {
      key: "status",
      header: t("sales.quotes.colStatus"),
      render: (row) => (
        <StatusChip
          variant={quoteStatusVariant(row.status)}
          data-testid="sales-quote-status"
          data-status={row.status}
        >
          {t(`sales.quoteStatus.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "total_minor",
      header: t("sales.quotes.colTotal"),
      numeric: true,
      presence: (loaded) => columnPresent(loaded, (row) => row.total_minor),
      render: (row) =>
        row.total_minor === undefined ? null : (
          <span className="num">{formatMinor(row.total_minor, row.currency, i18n.language)}</span>
        ),
    },
    {
      key: "valid_until",
      header: t("sales.quotes.colValid"),
      render: (row) =>
        row.valid_until === null ? null : formatBusinessDate(row.valid_until, i18n.language),
    },
    {
      key: "actions",
      header: t("sales.tasks.colActions"),
      actions: true,
      render: (row) =>
        canWrite && quoteDealChangeable(row.status) ? (
          <Button
            size="sm"
            data-testid="sales-deal-unlink"
            busy={unlink.isPending && unlinkingId === row.id}
            busyLabel={t("sales.pipeline.deal.unlinking")}
            disabled={unlink.isPending}
            onClick={() => {
              setUnlinkingId(row.id);
              unlink.mutate({ quoteId: row.id, opportunityId: null });
            }}
          >
            {t("sales.pipeline.deal.unlink")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      className="sales-360-card"
      data-testid="sales-deal-quotes"
      title={t("sales.pipeline.deal.quotesTitle")}
      action={
        canWrite ? (
          <div className="sales-toolbar">
            {canCreateQuote ? (
              <Button
                size="sm"
                data-testid="sales-deal-create-quote"
                onClick={() => {
                  void navigate({
                    to: "/sales/quotes/new",
                    search: { customer_id: deal.customer_id, opportunity_id: deal.id },
                  });
                }}
              >
                {t("sales.pipeline.deal.createQuote")}
              </Button>
            ) : null}
            {deal.status === "open" ? (
              <Button
                size="sm"
                data-testid="sales-deal-link-quote"
                onClick={() => {
                  unlink.reset();
                  setLinking(true);
                }}
              >
                {t("sales.pipeline.deal.linkQuote")}
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      <DataTable
        clientPager={bookClientPager(t, i18n.language)}
        data-testid="sales-deal-quote-table"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        caption={t("sales.pipeline.deal.quotesTitle")}
        rowTestId="sales-deal-quote"
        rowProps={(row) => ({ "data-quote-id": row.id, "data-status": row.status })}
        empty={
          <p className="sales-hint" data-testid="sales-deal-quotes-empty">
            {t("sales.pipeline.deal.quotesEmpty")}
          </p>
        }
      />
      {unlink.isError ? (
        <RefusalLine
          error={unlink.error}
          fallback={t("sales.pipeline.deal.unlinkFailed")}
          testId="sales-deal-unlink-failed"
        />
      ) : null}
      {linking ? (
        <LinkQuoteModal
          deal={deal}
          onClose={() => {
            setLinking(false);
          }}
        />
      ) : null}
    </Card>
  );
}
